import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { applyApiErrorsToForm } from '@/api/form-errors'
import { useChangePassword } from '@/account/api'
import { passwordChangeSchema, type PasswordChangeFormValues } from '@/account/schemas'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'

interface ErrorBanner {
  message: string
  traceId?: string
}

/**
 * `/account/password` — admin saját jelszó-csere (DEC-ADMIN-010).
 *
 * 3-mezős form (current + new + confirmation). Sikerre success-banner
 * + form-reset; a session-token **megmarad** (a backend nem revokálja).
 * Hibakezelés: 401 (rossz current_password) → banner; 422 → field-
 * mapping; egyéb → generic banner trace_id-vel.
 */
export function PasswordChangeForm() {
  const { t } = useTranslation()
  const [errorBanner, setErrorBanner] = useState<ErrorBanner | null>(null)
  const [success, setSuccess] = useState(false)

  const form = useForm<PasswordChangeFormValues>({
    resolver: zodResolver(passwordChangeSchema),
    defaultValues: {
      current_password: '',
      new_password: '',
      new_password_confirmation: '',
    },
  })

  const change = useChangePassword()

  function onSubmit(values: PasswordChangeFormValues) {
    setErrorBanner(null)
    setSuccess(false)
    change.mutate(values, {
      onSuccess: () => {
        setSuccess(true)
        form.reset({
          current_password: '',
          new_password: '',
          new_password_confirmation: '',
        })
      },
      onError: (error) => {
        const result = applyApiErrorsToForm(error, form.setError)
        if (result.handled) return
        if (result.code === 'unauthenticated') {
          setErrorBanner({
            message: t('account.passwordCurrentInvalid'),
            traceId: result.traceId,
          })
        } else {
          setErrorBanner({
            message: result.message ?? t('account.passwordError'),
            traceId: result.traceId,
          })
        }
      },
    })
  }

  return (
    <main className="mx-auto flex max-w-md flex-col gap-6 p-6">
      <header className="flex flex-col gap-1">
        <Link to="/" className="text-sm text-primary underline-offset-4 hover:underline self-start">
          {t('account.back')}
        </Link>
        <h1 className="text-2xl font-bold tracking-tight">{t('account.passwordTitle')}</h1>
        <p className="text-sm text-muted-foreground">{t('account.passwordIntro')}</p>
      </header>

      {success && (
        <Alert>
          <AlertDescription className="text-verified">
            {t('account.passwordSuccess')}
          </AlertDescription>
        </Alert>
      )}

      {errorBanner && (
        <Alert variant="destructive">
          <AlertDescription>
            {errorBanner.message}
            {errorBanner.traceId && (
              <span className="block text-xs opacity-70">trace ID: {errorBanner.traceId}</span>
            )}
          </AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <FormField
            control={form.control}
            name="current_password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('account.currentPasswordLabel')}</FormLabel>
                <FormControl>
                  <Input type="password" autoComplete="current-password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="new_password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('account.newPasswordLabel')}</FormLabel>
                <FormControl>
                  <Input type="password" autoComplete="new-password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="new_password_confirmation"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('account.newPasswordConfirmationLabel')}</FormLabel>
                <FormControl>
                  <Input type="password" autoComplete="new-password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" disabled={change.isPending} className="mt-2">
            {change.isPending ? t('account.passwordSubmitting') : t('account.passwordSubmit')}
          </Button>
        </form>
      </Form>
    </main>
  )
}
